import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';

import type MetricsDetailsController from 'webapp/controllers/metrics/details';

import type MetricDetailsService from 'webapp/services/metric-details';

// Entry for shared links (/metrics/details?state=…): opens the details drawer over the Metrics page.
export default class MetricsDetailsRoute extends Route {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('metric-details')
	declare metricDetailsService: MetricDetailsService;

	// Leaving closes the drawer, and its close must not redirect that transition to /metrics.
	// `activatedRoute` can't tell: it still reads 'metrics.details' when the drawer closes.
	private isLeaving = false;

	override beforeModel(transition: PublicTransition): Promise<unknown> | void {
		void this.mmScorecardsService.requestScorecards();
		void this.mmGoalsService.requestSpaceGoals();
		void this.mmGoalsService.requestSpaceGoalsData();
		void this.mmMetricsService.prepareForMetricEditing();

		// a shared forecast loads by its id, so the forecasts must be there before the drawer opens
		const encodedState = transition.to?.queryParams?.state;
		const state =
			typeof encodedState === 'string'
				? this.metricDetailsService.deserialize(encodedState)
				: null;
		if (state?.forecast?.forecastId) {
			return this.mmForecastsService.requestForecasts();
		}
	}

	override setupController(controller: MetricsDetailsController, model: unknown): void {
		super.setupController(controller, model);

		const state = controller.state
			? this.metricDetailsService.deserialize(controller.state)
			: null;
		controller.state = null;
		this.isLeaving = false;

		const popup = this.metricDetailsService.openMetricDetailsDrawer(state ? { state } : {});
		popup?.on(PopupEvent.CLOSING, this, () => {
			if (!this.isLeaving) {
				void this.navigationService.replaceRoute('metrics');
			}
		});
	}

	// Fires before the router's routeWillChange, where the navigation service closes all popups.
	@action
	override willTransition(_transition: PublicTransition): void {
		this.isLeaving = true;
	}

	override resetController(controller: MetricsDetailsController, isExiting: boolean): void {
		if (isExiting) {
			controller.state = null;
		}
	}
}
